import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import './About.scss';
import { urlFor, client } from '../../client';
import { AppWrap, MotionWrap } from '../../wrapper';
import { HiCode, HiDatabase, HiDeviceMobile, HiChip, HiLightningBolt } from 'react-icons/hi';

// Icon mapping for expertise areas
const iconMap = {
  'ML/AI': HiChip,
  'Machine Learning': HiChip,
  'AI': HiChip,
  'Database': HiDatabase,
  'Backend': HiDatabase,
  'Mobile': HiDeviceMobile,
  'App': HiDeviceMobile,
  'Web': HiCode,
  'Frontend': HiCode,
  'Full Stack': HiLightningBolt,
  'default': HiCode
};

const getIcon = (title) => {
  for (const [key, Icon] of Object.entries(iconMap)) {
    if (title?.toLowerCase().includes(key.toLowerCase())) {
      return Icon;
    }
  }
  return iconMap.default;
};

const About = () => {
  const [abouts, setAbouts] = useState([]);
  const [activeCard, setActiveCard] = useState(null);

  useEffect(() => {
    const query = '*[_type == "abouts"]';
    client.fetch(query).then((data) => {
      setAbouts(data);
    });
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 60, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  return (
    <div className="expertise">
      {/* Header Section */}
      <motion.div
        className="expertise__header"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        viewport={{ once: true }}
      >
        <motion.span 
          className="expertise__label"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          viewport={{ once: true }}
        >
          What I Do
        </motion.span>
        <motion.h2
          className="expertise__title"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          My <span className="gradient-text">Expertise</span>
        </motion.h2>
        <motion.p
          className="expertise__subtitle"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
        >
          Building innovative solutions at the intersection of AI, data, and user experience
        </motion.p>
      </motion.div>

      {/* Bento Grid */}
      <motion.div
        className="expertise__grid"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        {abouts.map((about, index) => {
          const Icon = getIcon(about.title);
          const isLarge = index === 0; // First card is featured/large
          
          return (
            <motion.div
              key={about.title + index}
              className={`expertise__card ${isLarge ? 'expertise__card--featured' : ''} ${activeCard === index ? 'active' : ''}`}
              variants={cardVariants}
              onHoverStart={() => setActiveCard(index)}
              onHoverEnd={() => setActiveCard(null)}
              whileHover={{ 
                y: -8,
                transition: { duration: 0.3, ease: "easeOut" }
              }}
            >
              {/* Gradient Border Effect */}
              <div className="expertise__card-border" />
              
              {/* Card Content */}
              <div className="expertise__card-content">
                {/* Icon */}
                <motion.div 
                  className="expertise__card-icon"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ duration: 0.3 }}
                >
                  <Icon />
                </motion.div>

                {/* Image (if available) */}
                {about.imgUrl && (
                  <div className="expertise__card-image">
                    <img src={urlFor(about.imgUrl)} alt={about.title} />
                  </div>
                )}

                {/* Text Content */}
                <div className="expertise__card-text">
                  <h3 className="expertise__card-title">{about.title}</h3>
                  <p className="expertise__card-description">{about.description}</p>
                </div>

                {/* Decorative Elements */}
                <div className="expertise__card-glow" />
              </div>

              {/* Card Number */}
              <span className="expertise__card-number">0{index + 1}</span>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Bottom Accent Line */}
      <motion.div
        className="expertise__accent-line"
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
        viewport={{ once: true }}
      />
    </div>
  );
};

export default AppWrap(
  MotionWrap(About, 'app__about'),
  'expertise',
  'app__whitebg'
);
